import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Icon } from "@cloudscape-design/components";
import "./TimeRange.css";

// ========================
// Types
// ========================
export type TimeRangeGranularity = "month" | "day";

export interface TimeRangeValue {
  /** Start date in format: YYYY-MM (month) or YYYY-MM-DD (day) */
  startDate: string;
  /** End date in format: YYYY-MM (month) or YYYY-MM-DD (day) */
  endDate: string;
}

export interface TimeRangeProps {
  /** Current value of the time range */
  value: TimeRangeValue | null;
  /** Callback when the range changes (auto-triggered on selection) */
  onChange: (value: TimeRangeValue | null) => void;
  /** Granularity: "month" (default) or "day" */
  granularity?: TimeRangeGranularity;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Minimum selectable date in YYYY-MM format */
  minDate?: string;
  /** Maximum selectable date in YYYY-MM format */
  maxDate?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Preset quick-select options (optional) */
  presets?: TimeRangePreset[];
}

export interface TimeRangePreset {
  label: string;
  getValue: () => TimeRangeValue;
}

// ========================
// Constants
// ========================
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MONTH_FULL_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ========================
// Helper functions
// ========================
const parseYearMonth = (dateStr: string): { year: number; month: number } => {
  const parts = dateStr.split("-");
  return { year: parseInt(parts[0], 10), month: parseInt(parts[1], 10) };
};

const formatDisplayValue = (value: TimeRangeValue | null): string => {
  if (!value) return "";
  const start = parseYearMonth(value.startDate);
  const end = parseYearMonth(value.endDate);
  return `${start.year}-${String(start.month).padStart(2, "0")} — ${end.year}-${String(end.month).padStart(2, "0")}`;
};

const isMonthDisabled = (
  year: number,
  month: number,
  minDate?: string,
  maxDate?: string
): boolean => {
  const val = year * 100 + month;
  if (minDate) {
    const min = parseYearMonth(minDate);
    if (val < min.year * 100 + min.month) return true;
  }
  if (maxDate) {
    const max = parseYearMonth(maxDate);
    if (val > max.year * 100 + max.month) return true;
  }
  return false;
};

const isMonthInRange = (
  year: number,
  month: number,
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number
): boolean => {
  const val = year * 100 + month;
  const start = startYear * 100 + startMonth;
  const end = endYear * 100 + endMonth;
  return val >= start && val <= end;
};

const isValidYYYYMM = (str: string): boolean => {
  return /^\d{4}\/(0[1-9]|1[0-2])$/.test(str);
};

// ========================
// TimeRange Component
// ========================
export const TimeRange: React.FC<TimeRangeProps> = ({
  value,
  onChange,
  granularity = "month",
  placeholder = "Filter by month",
  minDate,
  maxDate,
  disabled = false,
  presets,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Each panel has its own display year
  const now = new Date();
  const [leftDisplayYear, setLeftDisplayYear] = useState<number>(
    value ? parseYearMonth(value.startDate).year : now.getFullYear() - 1
  );
  const [rightDisplayYear, setRightDisplayYear] = useState<number>(
    value ? parseYearMonth(value.endDate).year : now.getFullYear()
  );

  // Draft state for start/end selection
  const [startYear, setStartYear] = useState<number>(
    value ? parseYearMonth(value.startDate).year : 0
  );
  const [startMonth, setStartMonth] = useState<number>(
    value ? parseYearMonth(value.startDate).month : 0
  );
  const [endYear, setEndYear] = useState<number>(
    value ? parseYearMonth(value.endDate).year : 0
  );
  const [endMonth, setEndMonth] = useState<number>(
    value ? parseYearMonth(value.endDate).month : 0
  );

  // Input field values
  const [startInput, setStartInput] = useState<string>(
    value ? `${parseYearMonth(value.startDate).year}/${String(parseYearMonth(value.startDate).month).padStart(2, "0")}` : ""
  );
  const [endInput, setEndInput] = useState<string>(
    value ? `${parseYearMonth(value.endDate).year}/${String(parseYearMonth(value.endDate).month).padStart(2, "0")}` : ""
  );

  // Sync internal state when value prop changes externally
  useEffect(() => {
    if (value) {
      const start = parseYearMonth(value.startDate);
      const end = parseYearMonth(value.endDate);
      setStartYear(start.year);
      setStartMonth(start.month);
      setEndYear(end.year);
      setEndMonth(end.month);
      setStartInput(`${start.year}/${String(start.month).padStart(2, "0")}`);
      setEndInput(`${end.year}/${String(end.month).padStart(2, "0")}`);
    } else {
      setStartYear(0);
      setStartMonth(0);
      setEndYear(0);
      setEndMonth(0);
      setStartInput("");
      setEndInput("");
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Auto-trigger onChange when both start and end are selected
  const tryAutoApply = useCallback((sYear: number, sMonth: number, eYear: number, eMonth: number) => {
    if (sYear > 0 && sMonth > 0 && eYear > 0 && eMonth > 0) {
      const startVal = sYear * 100 + sMonth;
      const endVal = eYear * 100 + eMonth;
      if (startVal <= endVal) {
        const newValue: TimeRangeValue = {
          startDate: `${sYear}-${String(sMonth).padStart(2, "0")}`,
          endDate: `${eYear}-${String(eMonth).padStart(2, "0")}`,
        };
        onChange(newValue);
      }
    }
  }, [onChange]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    if (!isOpen) {
      // Opening: reset display years
      if (value) {
        const start = parseYearMonth(value.startDate);
        const end = parseYearMonth(value.endDate);
        setLeftDisplayYear(start.year);
        setRightDisplayYear(end.year);
      } else {
        setLeftDisplayYear(now.getFullYear() - 1);
        setRightDisplayYear(now.getFullYear());
      }
    }
    setIsOpen((prev) => !prev);
  }, [disabled, isOpen, value]);

  const handleMonthClick = useCallback((year: number, month: number) => {
    // If no start selected, or both are selected (re-start), set as start
    if (startMonth === 0 || (startMonth > 0 && endMonth > 0)) {
      setStartYear(year);
      setStartMonth(month);
      setEndYear(0);
      setEndMonth(0);
      setStartInput(`${year}/${String(month).padStart(2, "0")}`);
      setEndInput("");
    } else {
      // Start is selected, set end
      const startVal = startYear * 100 + startMonth;
      const clickVal = year * 100 + month;

      if (clickVal >= startVal) {
        setEndYear(year);
        setEndMonth(month);
        setEndInput(`${year}/${String(month).padStart(2, "0")}`);
        tryAutoApply(startYear, startMonth, year, month);
      } else {
        // Clicked before start — make this the new start, old start becomes end
        setEndYear(startYear);
        setEndMonth(startMonth);
        setEndInput(`${startYear}/${String(startMonth).padStart(2, "0")}`);
        setStartYear(year);
        setStartMonth(month);
        setStartInput(`${year}/${String(month).padStart(2, "0")}`);
        tryAutoApply(year, month, startYear, startMonth);
      }
    }
  }, [startYear, startMonth, endYear, endMonth, tryAutoApply]);

  const handleStartInputBlur = useCallback(() => {
    if (isValidYYYYMM(startInput)) {
      const [y, m] = startInput.split("/").map(Number);
      setStartYear(y);
      setStartMonth(m);
      if (endYear > 0 && endMonth > 0) {
        tryAutoApply(y, m, endYear, endMonth);
      }
    }
  }, [startInput, endYear, endMonth, tryAutoApply]);

  const handleEndInputBlur = useCallback(() => {
    if (isValidYYYYMM(endInput)) {
      const [y, m] = endInput.split("/").map(Number);
      setEndYear(y);
      setEndMonth(m);
      if (startYear > 0 && startMonth > 0) {
        tryAutoApply(startYear, startMonth, y, m);
      }
    }
  }, [endInput, startYear, startMonth, tryAutoApply]);

  const handleClear = useCallback(() => {
    setStartYear(0);
    setStartMonth(0);
    setEndYear(0);
    setEndMonth(0);
    setStartInput("");
    setEndInput("");
    onChange(null);
    setIsOpen(false);
  }, [onChange]);

  const handleCancel = useCallback(() => {
    // Revert to current value
    if (value) {
      const start = parseYearMonth(value.startDate);
      const end = parseYearMonth(value.endDate);
      setStartYear(start.year);
      setStartMonth(start.month);
      setEndYear(end.year);
      setEndMonth(end.month);
      setStartInput(`${start.year}/${String(start.month).padStart(2, "0")}`);
      setEndInput(`${end.year}/${String(end.month).padStart(2, "0")}`);
    } else {
      setStartYear(0);
      setStartMonth(0);
      setEndYear(0);
      setEndMonth(0);
      setStartInput("");
      setEndInput("");
    }
    setIsOpen(false);
  }, [value]);

  const highlightRange = useMemo(() => {
    if (startYear > 0 && startMonth > 0 && endYear > 0 && endMonth > 0) {
      return { startYear, startMonth, endYear, endMonth };
    }
    return undefined;
  }, [startYear, startMonth, endYear, endMonth]);

  const displayValue = formatDisplayValue(value);

  // Render a single month grid for a given year
  const renderMonthGrid = (year: number) => (
    <div className="time-range-month-grid">
      {MONTH_NAMES.map((name, idx) => {
        const monthNum = idx + 1;
        const monthDisabled = isMonthDisabled(year, monthNum, minDate, maxDate);
        const isStart = startYear === year && startMonth === monthNum;
        const isEnd = endYear === year && endMonth === monthNum;
        const isSelected = isStart || isEnd;
        const inRange = highlightRange
          ? isMonthInRange(year, monthNum, highlightRange.startYear, highlightRange.startMonth, highlightRange.endYear, highlightRange.endMonth) && !isSelected
          : false;

        return (
          <button
            key={`${year}-${monthNum}`}
            type="button"
            className={[
              "time-range-month-cell",
              isSelected ? "selected" : "",
              inRange ? "in-range" : "",
              monthDisabled ? "disabled" : "",
            ].filter(Boolean).join(" ")}
            disabled={monthDisabled}
            onClick={() => !monthDisabled && handleMonthClick(year, monthNum)}
            aria-label={`${MONTH_FULL_NAMES[idx]} ${year}`}
          >
            {name}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="time-range-container" ref={containerRef}>
      {/* Trigger */}
      <div
        className={`time-range-trigger ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""}`}
        onClick={handleToggle}
        role="button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <Icon name="calendar" variant="subtle" />
        <span className={`time-range-trigger-text ${!displayValue ? "placeholder" : ""}`}>
          {displayValue || placeholder}
        </span>
        {value && !disabled && (
          <span
            className="time-range-clear-icon"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            role="button"
            aria-label="Clear time range"
          >
            <Icon name="close" variant="subtle" size="small" />
          </span>
        )}
        <Icon name="caret-down-filled" variant="subtle" size="small" />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="time-range-dropdown" role="dialog" aria-label="Time range picker">
          {/* Month grids with independent navigation */}
          <div className="time-range-grids-row">
            <div className="time-range-panel">
              <div className="time-range-panel-header">
                <button
                  className="time-range-nav-btn"
                  onClick={() => setLeftDisplayYear(leftDisplayYear - 1)}
                  type="button"
                  aria-label="Previous year"
                >
                  <Icon name="angle-left" />
                </button>
                <span className="time-range-year-title">{leftDisplayYear}</span>
                <button
                  className="time-range-nav-btn"
                  onClick={() => setLeftDisplayYear(leftDisplayYear + 1)}
                  type="button"
                  aria-label="Next year"
                >
                  <Icon name="angle-right" />
                </button>
              </div>
              {renderMonthGrid(leftDisplayYear)}
            </div>
            <div className="time-range-panel">
              <div className="time-range-panel-header">
                <button
                  className="time-range-nav-btn"
                  onClick={() => setRightDisplayYear(rightDisplayYear - 1)}
                  type="button"
                  aria-label="Previous year"
                >
                  <Icon name="angle-left" />
                </button>
                <span className="time-range-year-title">{rightDisplayYear}</span>
                <button
                  className="time-range-nav-btn"
                  onClick={() => setRightDisplayYear(rightDisplayYear + 1)}
                  type="button"
                  aria-label="Next year"
                >
                  <Icon name="angle-right" />
                </button>
              </div>
              {renderMonthGrid(rightDisplayYear)}
            </div>
          </div>

          {/* Input fields */}
          <div className="time-range-inputs">
            <div className="time-range-input-group">
              <label className="time-range-input-label">Start month</label>
              <input
                type="text"
                className="time-range-input"
                placeholder="YYYY/MM"
                value={startInput}
                onChange={(e) => setStartInput(e.target.value)}
                onBlur={handleStartInputBlur}
                onKeyDown={(e) => { if (e.key === "Enter") handleStartInputBlur(); }}
              />
            </div>
            <div className="time-range-input-group">
              <label className="time-range-input-label">End month</label>
              <input
                type="text"
                className="time-range-input"
                placeholder="YYYY/MM"
                value={endInput}
                onChange={(e) => setEndInput(e.target.value)}
                onBlur={handleEndInputBlur}
                onKeyDown={(e) => { if (e.key === "Enter") handleEndInputBlur(); }}
              />
            </div>
          </div>

          {/* Footer actions */}
          <div className="time-range-footer">
            <button type="button" className="time-range-footer-link clear" onClick={handleClear}>
              Clear
            </button>
            <button type="button" className="time-range-footer-link cancel" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeRange;
